import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, ShoppingBag, Users, DollarSign, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import API, { formatPrice, CATEGORY_LABELS, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";
import ContentEditor from "@/components/admin/ContentEditor";
import PagesEditor from "@/components/admin/PagesEditor";
import MediaLibrary from "@/components/admin/MediaLibrary";

const EMPTY = { title: "", category: "talleres", short_description: "", description: "", price: "", image_url: "", duration: "", modality: "Online", instructor: "", featured: false, active: true };

const inputCls = "mt-1.5 w-full rounded-xl border border-[#4b3a2f]/15 bg-white px-3.5 py-3 text-sm outline-none focus:border-[#c9a24b]";

const Admin = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [s, p, o, u] = await Promise.all([
        API.get("/admin/stats"), API.get("/admin/products"), API.get("/admin/orders"), API.get("/admin/users"),
      ]);
      setStats(s.data); setProducts(p.data.products); setOrders(o.data.orders); setUsers(u.data.users);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { if (user?.role === "admin") loadAll(); }, [user]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setDialogOpen(true); };

  const save = async () => {
    if (!form.title.trim() || form.price === "" || Number(form.price) <= 0) {
      toast.error("Título y precio válido son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) };
      if (editing) {
        await API.put(`/admin/products/${editing.id}`, payload);
        toast.success("Producto actualizado");
      } else {
        await API.post("/admin/products", payload);
        toast.success("Producto creado");
      }
      setDialogOpen(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await API.delete(`/admin/products/${p.id}`);
      toast.success("Producto eliminado");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#faf7f2] pt-[72px]">
        <p className="text-[#6b5b52]">Acceso solo para administradores.</p>
      </div>
    );
  }

  const statsCards = [
    { icon: Package, label: "Productos", value: stats?.products ?? "—" },
    { icon: ShoppingBag, label: "Pedidos", value: stats?.orders ?? "—" },
    { icon: Users, label: "Usuarios", value: stats?.users ?? "—" },
    { icon: DollarSign, label: "Ingresos", value: stats ? formatPrice(stats.revenue) : "—" },
  ];

  return (
    <div className="min-h-screen bg-[#faf7f2] pb-24 pt-[120px]" data-testid="admin-page">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono-d text-[11px] uppercase tracking-[0.28em] text-[#a47b52]">Gestión AWEN</p>
            <h1 className="mt-3 font-serif-d text-5xl font-semibold text-[#4b3a2f]">Panel de administrador</h1>
          </div>
          <button
            onClick={openCreate}
            data-testid="admin-add-product-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#c9a24b] px-6 py-3.5 text-sm font-semibold text-[#4b3a2f] transition-colors hover:bg-[#b8913b]"
          >
            <Plus size={16} /> Nuevo producto
          </button>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {statsCards.map((s) => (
            <div key={s.label} className="rounded-2xl border border-[#e8ded4] bg-white p-6">
              <s.icon size={20} className="text-[#c9a24b]" />
              <p className="mt-4 font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#a47b52]">{s.label}</p>
              <p className="mt-1 font-serif-d text-3xl font-semibold text-[#4b3a2f]">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex gap-2">
          {[["products", "Productos"], ["orders", "Pedidos"], ["users", "Usuarios"], ["content", "Contenido"], ["pages", "Páginas"], ["media", "Multimedia"]].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`rounded-full px-6 py-2.5 text-sm font-medium transition-colors ${tab === id ? "bg-[#4b3a2f] text-[#f2e9dd]" : "bg-white text-[#6b5b52] border border-[#e8ded4]"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "content" && <ContentEditor />}
        {tab === "pages" && <PagesEditor />}
        {tab === "media" && <MediaLibrary />}

        {tab === "products" && (
          <div className="mt-6 overflow-hidden rounded-2xl border border-[#e8ded4] bg-white">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="bg-[#f2e9dd] text-[11px] uppercase tracking-wider text-[#a47b52]">
                  <tr>
                    <th className="px-5 py-4">Producto</th>
                    <th className="px-5 py-4">Categoría</th>
                    <th className="px-5 py-4">Precio</th>
                    <th className="px-5 py-4">Estado</th>
                    <th className="px-5 py-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody data-testid="admin-products-table">
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-[#e8ded4] hover:bg-[#faf7f2]">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <img src={p.image_url} alt="" className="h-12 w-12 rounded-lg object-cover" />
                          <span className="font-medium text-[#4b3a2f]">{p.title}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-[#6b5b52]">{CATEGORY_LABELS[p.category]}</td>
                      <td className="px-5 py-4 font-semibold text-[#4b3a2f]">{formatPrice(p.price)}</td>
                      <td className="px-5 py-4">
                        <span className={`rounded-full px-3 py-1 text-xs ${p.active ? "bg-[#3b7a57]/10 text-[#3b7a57]" : "bg-[#c85a48]/10 text-[#c85a48]"}`}>
                          {p.active ? "Activo" : "Oculto"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.id}`} aria-label="Editar" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#4b3a2f]/15 text-[#6b5b52] transition-colors hover:bg-[#c9a24b] hover:text-white">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => del(p)} data-testid={`admin-delete-${p.id}`} aria-label="Eliminar" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#c85a48]/30 text-[#c85a48] transition-colors hover:bg-[#c85a48] hover:text-white">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-6 space-y-3" data-testid="admin-orders-list">
            {orders.length === 0 && <p className="rounded-2xl bg-white p-8 text-center text-[#6b5b52]">Aún no hay pedidos.</p>}
            {orders.map((o) => (
              <div key={o.id} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#e8ded4] bg-white p-5">
                <div>
                  <p className="font-medium text-[#4b3a2f]">{o.user_name} · <span className="text-[#6b5b52]">{o.user_email}</span></p>
                  <p className="mt-1 text-xs text-[#6b5b52]">{o.items.map((i) => `${i.title} ×${i.quantity}`).join(" · ")}</p>
                  <p className="mt-1 font-mono-d text-[10px] uppercase tracking-widest text-[#a47b52]">{new Date(o.created_at).toLocaleString("es-MX")}</p>
                </div>
                <div className="flex items-center gap-4">
                  <p className="font-serif-d text-xl font-semibold text-[#4b3a2f]">{formatPrice(o.total)}</p>
                  <span className={`rounded-full px-3 py-1 text-xs ${o.status === "paid" ? "bg-[#3b7a57]/10 text-[#3b7a57]" : "bg-[#a47b52]/10 text-[#a47b52]"}`} data-testid={`admin-order-status-${o.id}`}>
                    {o.status === "paid" ? "Pagado" : "Pendiente"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "users" && (
          <div className="mt-6 overflow-hidden rounded-2xl border border-[#e8ded4] bg-white">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="bg-[#f2e9dd] text-[11px] uppercase tracking-wider text-[#a47b52]">
                <tr>
                  <th className="px-5 py-4">Nombre</th>
                  <th className="px-5 py-4">Correo</th>
                  <th className="px-5 py-4">WhatsApp</th>
                  <th className="px-5 py-4">Ciudad, País</th>
                  <th className="px-5 py-4">Rol</th>
                </tr>
              </thead>
              <tbody data-testid="admin-users-table">
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-[#e8ded4]">
                    <td className="px-5 py-4 font-medium text-[#4b3a2f]">{u.name}</td>
                    <td className="px-5 py-4 text-[#6b5b52]">{u.email}</td>
                    <td className="px-5 py-4 text-[#6b5b52]">{u.whatsapp}</td>
                    <td className="px-5 py-4 text-[#6b5b52]">{u.city}, {u.country}</td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-3 py-1 text-xs ${u.role === "admin" ? "bg-[#c9a24b]/15 text-[#a47b52]" : "bg-[#4b3a2f]/5 text-[#6b5b52]"}`}>
                        {u.role === "admin" ? "Admin" : "Cliente"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto rounded-3xl bg-white" data-testid="admin-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-d text-3xl text-[#4b3a2f]">
              {editing ? "Editar producto" : "Nuevo producto"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Título *</span>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-form-title" className={inputCls} />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Categoría *</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-form-category" className={inputCls}>
                {Object.entries(CATEGORY_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Precio USD *</span>
              <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="admin-form-price" className={inputCls} />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">URL de la foto</span>
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" data-testid="admin-form-image" className={inputCls} />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Descripción corta</span>
              <input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} data-testid="admin-form-shortdesc" className={inputCls} />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Descripción completa</span>
              <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-form-description" className={inputCls} />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Duración</span>
              <input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="50 minutos" data-testid="admin-form-duration" className={inputCls} />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Modalidad</span>
              <input value={form.modality} onChange={(e) => setForm({ ...form, modality: e.target.value })} placeholder="Online" data-testid="admin-form-modality" className={inputCls} />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Instructor / Especialista</span>
              <input value={form.instructor} onChange={(e) => setForm({ ...form, instructor: e.target.value })} data-testid="admin-form-instructor" className={inputCls} />
            </label>
            <div className="flex items-end gap-6 pb-1">
              <label className="flex items-center gap-2 text-sm text-[#4b3a2f]">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="admin-form-featured" className="h-4 w-4 accent-[#c9a24b]" />
                Destacado
              </label>
              <label className="flex items-center gap-2 text-sm text-[#4b3a2f]">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="admin-form-active" className="h-4 w-4 accent-[#c9a24b]" />
                Activo
              </label>
            </div>
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="admin-form-save-button"
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#4b3a2f] py-4 text-sm font-semibold text-[#f2e9dd] transition-colors hover:bg-[#c9a24b] hover:text-[#4b3a2f]"
          >
            {saving && <Loader2 size={16} className="animate-spin" />} {editing ? "Guardar cambios" : "Crear producto"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
